"use client"

import { localizeHrefForLocale } from "@/app/(site)/_utils/translateCanonicalSegments"
import { combinedLocales } from "@/i18n/locales"
import { UNIVERSAL_DEFAULT_LANGUAGE } from "@/lib/siteconfig/constants"
import Link, { type LinkProps } from "next/link"
import { usePathname } from "next/navigation"
import React from "react"

interface DynamicLinkProps extends LinkProps {
    children: React.ReactNode
    prefetch?: boolean | "auto" | null
    href: string
    className?: string
    role?: React.AriaRole | undefined
    [key: string]: any
}

const childrenContainsLink = (node: React.ReactNode): boolean => {
    if (!node) return false
    if (Array.isArray(node)) return node.some(childrenContainsLink)

    if (!React.isValidElement(node)) return false

    if (node.type === "a") return true
    if (node.type === Link) return true

    const props = node.props as { href?: string; children?: React.ReactNode }
    if (typeof props?.href === "string") return true

    return childrenContainsLink(props?.children)
}

const DynamicLink: React.FC<DynamicLinkProps> = ({
    children,
    prefetch = true,
    href: hrefProp,
    className,
    role,
    ...linkProps
}) => {
    let href = hrefProp
    const path = usePathname()

    if (typeof href === "object") {
        // If we receive an unresolved reference from Sanity we do not render the link.
        if ("_ref" in href) {
            console.warn(`Received unresolved Sanity reference in DynamicLink.`, href)
            return null
        }

        href = hrefProp.toString()
    }

    const shouldNotPrefetch = process.env.NEXT_PUBLIC_PREFETCH === "false"
    const currentLocale = path.split("/")[1]
    const resolvedLocale = combinedLocales.includes(currentLocale)
        ? currentLocale
        : UNIVERSAL_DEFAULT_LANGUAGE

    const baseHref = href.startsWith("/") ? href : `/${href}`
    const localizedHref = localizeHrefForLocale(baseHref, resolvedLocale).toLowerCase()
    const uri = `/${resolvedLocale}${localizedHref}`

    if (childrenContainsLink(children)) {
        return <div className={className}>{children}</div>
    }

    return (
        <Link
            role={role}
            {...linkProps}
            className={className}
            href={uri}
            prefetch={shouldNotPrefetch ? false : prefetch}
        >
            {children}
        </Link>
    )
}

export default DynamicLink
